import React from 'react'
import { Instagram, Mail, Youtube } from 'lucide-react'
import { useContent } from '../contexts/ContentContext'
import { branding } from '../content/branding'
import LazyImage from './ui/LazyImage'
import { useNavigation } from '../contexts/NavigationContext'

const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
    <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
  </svg>
)

const SpotifyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.5 17.3a.75.75 0 0 1-1 .25c-2.8-1.7-6.3-2.1-10.4-1.1a.75.75 0 0 1-.35-1.45c4.5-1.1 8.4-.65 11.5 1.3a.75.75 0 0 1 .25 1zm1.5-3.3a.94.94 0 0 1-1.3.3c-3.2-2-8-2.55-11.8-1.4a.94.94 0 0 1-.55-1.8c4.3-1.3 9.6-.7 13.3 1.6.45.3.6.9.35 1.3zm.15-3.4c-3.8-2.3-10.1-2.5-13.75-1.4a1.13 1.13 0 0 1-.65-2.15c4.2-1.3 11.1-1 15.5 1.6a1.13 1.13 0 0 1-1.1 1.95z"/>
  </svg>
)

const AppleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 12.54c-.03-2.71 2.22-4.01 2.32-4.07-1.26-1.85-3.23-2.1-3.93-2.13-1.67-.17-3.26 1-4.11 1-.87 0-2.17-.98-3.56-.95-1.83.03-3.52 1.06-4.46 2.7-1.9 3.3-.49 8.18 1.37 10.86.9 1.31 1.98 2.78 3.38 2.73 1.36-.06 1.87-.88 3.51-.88 1.63 0 2.1.88 3.53.85 1.46-.03 2.38-1.33 3.27-2.65 1.03-1.52 1.46-2.99 1.49-3.07-.03-.01-2.85-1.1-2.88-4.39zM14.32 4.68c.74-.9 1.24-2.15 1.1-3.4-1.07.04-2.37.71-3.14 1.6-.68.79-1.29 2.07-1.13 3.3 1.2.09 2.42-.61 3.17-1.5z"/>
  </svg>
)

const CONTACT_ICONS = {
  whatsapp:  <WhatsAppIcon />,
  mail:      <Mail      size={24} strokeWidth={1.5} />,
  instagram: <Instagram size={24} strokeWidth={1.5} />,
}

const SOCIAL_ICONS = {
  spotify: <SpotifyIcon />,
  apple:   <AppleIcon />,
  youtube: <Youtube size={24} strokeWidth={1.5} />,
}

export default function Footer() {
  const content = useContent()
  const { header: siteHeader, footer: footerContent } = content
  const data = footerContent
  const nav = siteHeader.nav
  const year = new Date().getFullYear()
  const { navigateTo } = useNavigation()

  const scrollToBooking = (e) => {
    e.preventDefault()
    window.dispatchEvent(new Event('booking:open'))
  }

  return (
    <footer className="bg-color-primary text-primary-on-dark snap-section" style={{ scrollSnapAlign: 'end' }} data-testid="site-footer">
      <div className="py-8 md:py-12 lg:py-14">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">

          {/* 1 — Navigation Links (top row) */}
          <div className="mb-6 md:mb-8 flex flex-wrap justify-center gap-x-5 gap-y-1 md:gap-x-6 lg:gap-x-8">
            {nav.map((item) => (
              <a
                key={item.anchor}
                href={item.anchor}
                onClick={(e) => {
                  e.preventDefault()
                  navigateTo(item.anchor)
                }}
                className="typography-menu-text-on-dark hover:text-primary-on-dark transition-colors min-h-[40px] inline-flex items-center"
                data-testid={`footer-nav-${item.anchor.replace('#', '')}`}
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* ———————— DIVIDER ———————— */}
          <div className="h-px bg-color-border-light mb-8 md:mb-10"></div>

          {/* 2 — Main Row: Brand + 3 Columns */}
          <div className="grid grid-cols-3 gap-4 md:gap-6 lg:grid-cols-[2fr_1fr_1fr_1fr] lg:gap-x-8">

            {/* Brand (Logo + Tagline) */}
            <div className="col-span-3 lg:col-span-1 flex flex-col items-center lg:items-start gap-3 lg:gap-4 mb-6 lg:mb-0 text-center lg:text-left">
              <LazyImage
                src={branding.logo.white}
                alt={branding.logo.alt}
                className="h-[28px] md:h-[32px] lg:h-[34px] w-auto object-contain"
              />
              <p className="typography-meta-on-dark italic whitespace-pre-line">
                {data.tagline}
              </p>
            </div>

            {/* Col 1 — Rechtliches */}
            <div className="text-left lg:text-left">
              <p className="typography-label-on-dark mb-3 md:mb-4 whitespace-nowrap text-[10px] tracking-[0.08em] md:text-label md:tracking-[0.2em]">
                {footerContent.legalLabel}
              </p>
              <div className="flex flex-col gap-1 md:gap-2">
                {data.legalLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="typography-menu-text-on-dark hover:text-primary-on-dark transition-colors min-h-[36px] inline-flex items-center"
                    data-testid={`footer-legal-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Col 2 — Folgen */}
            <div className="text-left lg:text-left">
              <p className="typography-label-on-dark mb-3 md:mb-4 whitespace-nowrap text-[10px] tracking-[0.08em] md:text-label md:tracking-[0.2em]">
                {footerContent.followLabel}
              </p>
              <div className="flex flex-col gap-1 md:gap-2">
                {data.social.map((s) => {
                  const iconKey = s.label.toLowerCase().split(' ')[0]
                  const icon = SOCIAL_ICONS[iconKey]
                  return (
                    <a
                      key={s.label}
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="typography-menu-text-on-dark hover:text-primary-on-dark transition-colors flex items-center gap-1.5 md:gap-2 min-h-[36px]"
                      aria-label={s.label}
                      data-testid={`footer-social-${s.label.toLowerCase().replace(/ /g, '-')}`}
                    >
                      {icon && (
                        <span className="w-5 h-5 flex items-center justify-center shrink-0 [&_svg]:w-full [&_svg]:h-full">
                          {icon}
                        </span>
                      )}
                      <span className="hidden md:inline">{s.label}</span>
                    </a>
                  )
                })}
              </div>
            </div>

            {/* Col 3 — Reach Out */}
            <div className="text-left lg:text-left">
              <p className="typography-label-on-dark mb-3 md:mb-4 whitespace-nowrap text-[10px] tracking-[0.08em] md:text-label md:tracking-[0.2em]">
                REACH OUT
              </p>
              <div className="flex flex-col gap-1 md:gap-2">
                {data.contact.map((c) => (
                  <a
                    key={c.label}
                    href={c.url}
                    target={c.url.startsWith('mailto') ? undefined : '_blank'}
                    rel="noreferrer"
                    className="typography-menu-text-on-dark hover:text-primary-on-dark transition-colors flex items-center gap-1.5 md:gap-2 min-h-[36px]"
                    aria-label={c.label}
                    data-testid={`footer-contact-${c.icon}`}
                  >
                    <span className="w-5 h-5 md:w-5 md:h-5 flex items-center justify-center shrink-0 [&_svg]:w-full [&_svg]:h-full">
                      {CONTACT_ICONS[c.icon]}
                    </span>
                    <span className="hidden md:inline">{c.label}</span>
                  </a>
                ))}
              </div>
            </div>

          </div>

          {/* ———————— DIVIDER + Disclaimer ———————— */}
          <div className="pt-8 md:pt-10 pb-6 md:pb-8 text-center">
            <div className="h-px bg-color-border-light mb-6 md:mb-8"></div>
            <h3 className="typography-label-on-dark font-semibold mb-3">
              {footerContent.disclaimerTitle}
            </h3>
            <p className="typography-meta-on-dark max-w-full md:max-w-3xl mx-auto">
              {footerContent.disclaimer.split('. ').map((sentence, index, array) => {
              if (sentence.includes(footerContent.disclaimerLinkKeyword)) {
                const [before, after] = sentence.split(footerContent.disclaimerLinkKeyword)
                return (
                  <React.Fragment key={index}>
                    {before}
                    <a
                      href="#booking"
                      onClick={scrollToBooking}
                      className="underline text-secondary-on-dark hover:text-primary-on-dark transition-colors"
                      data-testid="footer-erstgespraech-link"
                    >
                      {footerContent.introCallLabel}
                    </a>
                    {after}
                    {index < array.length - 1 && '. '}
                  </React.Fragment>
                )
              }
              return (
                <React.Fragment key={index}>
                  {sentence}
                  {index < array.length - 1 ? '. ' : '.'}
                </React.Fragment>
              )
              })}
            </p>
          </div>

          {/* ———————— DIVIDER + Copyright ———————— */}
          <div className="h-px bg-color-border-light mb-6 md:mb-8"></div>

          <div className="typography-meta-on-dark text-center">
            {`© ${year} Benjamin Kurtz Academy LLC. ${footerContent.copyright.prefix} `}
            <a
              href={footerContent.copyright.homeUrl}
              target="_blank"
              rel="noreferrer"
              className="text-secondary-on-dark hover:text-primary-on-dark underline transition-colors"
            >
              {footerContent.copyright.homeLabel}
            </a>
          </div>

        </div>
      </div>
    </footer>
  )
}
